import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, replace } from 'lodash';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import Divider from 'common-ui-components/dist/components/Atoms/Divider';
import Label from 'common-ui-components/dist/components/Atoms/Label';
import Modal from 'common-ui-components/dist/components/Atoms/Modal';
import PromoCode from 'common-ui-components/dist/components/Molecules/PromoCode';
import OrderSummaryLineItem from '../OrderSummaryLineItem';
import Promotional from '../Promotional';
import { USER_STATE_LOGGED_IN, QUOTE_PAGE_CONTEXT } from '../../../global/constants';
import SignInView from '../SignInView';
import { getLabelWithParam, isEProcurementApp } from '../../../utils/utils';
import { isFreightEnabled, replaceAll } from '../../../utils/utilFunctions';
import ShareCart from '../ShareCart';
import './OrderSummaryCart.scss';

const promoCodesHolder = {};
let defaultCode = '';

const OrderSummaryCart = ({
  className,
  labels,
  data,
  userState,
  userIcon,
  collapseIcon,
  openIcon,
  signInUrl,
  isCheckout,
  isCart,
  isOrderConfirmation,
  pageContext,
  addPromoCode,
  getpromoCodeDetails,
  promotionIconUrl,
  errorIcon,
  closeIcon,
  showPromotionalCode,
  isQuoteToOrder,
  includeTaxWithPrice,
  shareCartIcon,
  isShareCartEnabled,
  handleShareCartButtonClick,
  openShippingOptionModal,
  shippingInfo,
  renderItemSummaryContainer,
  isPromotionDisabled,
  renderTaxExemptCertificate,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showPromoDetails, setShowPromoDetails] = useState(false);
  const [promoCodeDetails, setPromoCodeDetails] = useState('');
  const itemText = data.totalItems === 1 ? labels.item : labels.items;
  const subtotalText = `${labels.subtotal} (${data.totalItems} ${itemText})`;
  const promos = data.coupons ? data.coupons : [];
  const getCouponError = () =>
    data.couponsError && data.couponsError.length > 0 ? data.couponsError[0] : {};
  const error = getCouponError();
  const promoCodeDescription = data.promoCodeDetails ? data.promoCodeDetails : {};

  const modalHeading = <Notification title={labels.promotionDetails} icon={promotionIconUrl} />;
  const removePromoCode = promoValue => {
    setShowPromoDetails(false);
    setPromoCodeDetails('');
    setErrorMessage('');

    const coupons = [];
    coupons.push({
      code: promoValue,
    });
    const couponsData = { coupons };
    const obj = {
      body: couponsData,
      remove: true,
    };
    addPromoCode(obj);
  };

  const showPromoCodeDetails = code => {
    setPromoCodeDetails('');
    if (promoCodesHolder[code]) {
      setPromoCodeDetails(promoCodesHolder[code]);
    } else {
      defaultCode = code;
      getpromoCodeDetails(code);
    }
    setShowPromoDetails(true);
  };

  React.useEffect(() => {
    if (!isEmpty(promoCodeDescription.terms)) {
      setPromoCodeDetails(promoCodeDescription.terms);
      if (!promoCodesHolder[defaultCode]) {
        promoCodesHolder[defaultCode] = promoCodeDescription.terms;
        promoCodeDescription.terms = '';
      }
    }
    const invalidPromos = promos.filter(promo => !promo.isValid);

    if (!isEmpty(error.code)) {
      setErrorMessage(
        labels[`${error.code}_Description`]
          ? labels[`${error.code}_Description`]
          : error.description
      );
    } else if (invalidPromos.length) {
      setErrorMessage(
        getLabelWithParam(labels.inValidPromoError, [invalidPromos.map(p => p.code).join(',')])
      );
    } else {
      setErrorMessage('');
    }
  }, [promoCodeDescription, promos, error]);

  React.useEffect(() => {
    if (isEmpty(error.code)) {
      const errMessage = errorMessage;
      setErrorMessage(errMessage);
    }
  }, [errorMessage]);

  const { details } = labels;

  const getPromoCodes = () =>
    promos.length > 0 ? (
      <div className="promoContainer" data-locator="promo-tag-container">
        {promos.map(promo => {
          const { code } = promo;
          const promoCodeProps = {
            key: code,
            promoCode: code,
            details,
            showPromoCodeDetails: () => showPromoCodeDetails(code),
            removePromoCode: () => removePromoCode(code),
            closeIcon,
          };
          return <PromoCode {...promoCodeProps} />;
        })}
      </div>
    ) : null;

  const getCartShippingAndHandling = () => {
    if (isEProcurementApp() && !isFreightEnabled()) return null;
    const { totalShippingAndHandling, totalTax, totalTaxValue } = data;
    const { shippingHandling, estimatedTax } = labels;
    return (
      <>
        {totalShippingAndHandling && (
          <OrderSummaryLineItem
            priceLabel={shippingHandling}
            price={totalShippingAndHandling}
            elementLocator="total-shipping-handling "
          />
        )}
        {totalTax && !isEProcurementApp() && (
          <>
            <OrderSummaryLineItem
              priceLabel={estimatedTax}
              price={totalTax}
              elementLocator="estimated-tax"
            />
            {totalTaxValue > 0 && renderTaxExemptCertificate}
          </>
        )}
      </>
    );
  };

  const getShippingAndHandling = () =>
    (isCheckout || isOrderConfirmation) && (
      <>
        <OrderSummaryLineItem
          priceLabel={labels.shippingHandling}
          price={data.totalShippingAndHandling}
          elementLocator="total-shipping-handling "
        />
        <OrderSummaryLineItem
          priceLabel={labels.estimatedTax}
          price={data.totalTax}
          elementLocator="estimated-tax"
        />
        {data.totalTaxValue > 0 && renderTaxExemptCertificate}
      </>
    );

  const getShippingAndHandlingLineItem = () => {
    if (!includeTaxWithPrice) {
      return isCart ? getCartShippingAndHandling() : getShippingAndHandling();
    }
    return null;
  };

  const checkForOrderConfirmation = userState === USER_STATE_LOGGED_IN && data.totalDiscounts;

  const getDiscountLineItem = () =>
    checkForOrderConfirmation && (
      <OrderSummaryLineItem
        priceLabel={isCheckout ? labels.discount : `${labels.discount}*`}
        price={data.totalDiscounts}
        className="order-summary-discount-line"
        elementLocator="total-discount"
      />
    );

  const getDivider = () => <Divider type="h-large" className="order-summary-large-divider" />;

  const getTitleLables = () => {
    const titleText =
      pageContext === QUOTE_PAGE_CONTEXT ? labels.quoteSummary : labels.orderSummary;
    return <Label title={titleText} />;
  };

  const getOrderTotal = () => {
    const { totalPrice, totalPriceWithTax } = data;
    const { preOrderTotal, cartOrderTotal } = labels;
    let priceLabel = pageContext ? labels[`${pageContext}Total`] : cartOrderTotal;
    let price = totalPriceWithTax;
    if (isEProcurementApp()) {
      priceLabel = pageContext ? labels[`${pageContext}Total`] : preOrderTotal;
      price = pageContext ? totalPriceWithTax : totalPrice;
      if (includeTaxWithPrice && typeof totalPriceWithTax !== 'undefined') {
        priceLabel = pageContext ? labels[`${pageContext}Total`] : cartOrderTotal;
        price = totalPriceWithTax;
      }
    }
    return (
      <OrderSummaryLineItem
        priceLabel={priceLabel}
        price={price}
        isOrderTotal
        elementLocator="total-price"
      />
    );
  };

  const getSubTotal = () => {
    const { subTotal, totalBasePriceWithTax } = data;
    let subTotalValue = subTotal;
    if (includeTaxWithPrice && typeof totalBasePriceWithTax !== 'undefined') {
      subTotalValue = totalBasePriceWithTax;
    }
    return typeof subTotalValue !== 'undefined' ? (
      <OrderSummaryLineItem
        priceLabel={subtotalText}
        price={subTotalValue}
        elementLocator="sub-total"
      />
    ) : null;
  };

  const renderPromoCodeSection = () => (
    <>
      <Promotional
        header={labels.promotionalCode}
        collapseIcon={collapseIcon}
        openIcon={openIcon}
        addPromoCode={addPromoCode}
        promos={promos}
        labels={labels}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        errorIcon={errorIcon}
      />
      {!isEmpty(errorMessage) && (
        <Notification
          className="promo-error"
          variation="inline"
          icon={errorIcon}
          type="error"
          description={errorMessage}
          elementLocator="promo-error"
        />
      )}
      {getPromoCodes()}
      <Modal
        className="modal__Content"
        shouldCloseOnOverlayClick
        isOpen={showPromoDetails}
        {...{ onRequestClose: () => setShowPromoDetails(false) }}
        heading={modalHeading}
      >
        {promoCodeDetails}
      </Modal>
    </>
  );

  const getShareCart = () =>
    isShareCartEnabled && (
      <ShareCart
        labels={labels}
        shareCartIcon={shareCartIcon}
        handleShareCartButtonClick={handleShareCartButtonClick}
      />
    );

  const getShippingInfo = () => {
    const shippingInfoLabel = get(labels, 'shippingInfo', '');
    const isDisplayedShippingInfo = (isEProcurementApp() && !isFreightEnabled()) || !isCart;

    if (
      isEmpty(data.totalShippingAndHandling) ||
      isDisplayedShippingInfo ||
      isEmpty(shippingInfoLabel)
    )
      return null;

    let responseText = replaceAll(shippingInfo, get(labels, 'shippingInfo', ''));
    if (isEmpty(shippingInfo[1])) {
      responseText = replace(responseText, ' ,', '');
    }

    return (
      <div
        className={`order-summary-shipping-info ${
          isEProcurementApp()
            ? 'order-summary-shipping-info-eproc'
            : 'order-summary-shipping-info-ecom'
        }`}
        data-locator="order-summary-shipping-info"
      >
        {responseText}
      </div>
    );
  };

  const getPromotionSection = () => {
    const disablePromotions = !isOrderConfirmation && showPromotionalCode && !isPromotionDisabled;

    return (
      disablePromotions && (
        <div className={isQuoteToOrder ? 'promo-code-disabled' : ''}>
          {renderPromoCodeSection()}
        </div>
      )
    );
  };

  return (
    <div className={`order-summary-cart ${className}`} data-locator="order-summary-cart-details">
      {userState !== USER_STATE_LOGGED_IN && (
        <div className="order-summary-signIn">
          <SignInView
            signIn={labels.signIn}
            yourPrice={labels.yourPrice}
            signInViewMessage={labels.signInViewMessage}
            userIcon={userIcon}
            signInUrl={signInUrl}
          />
        </div>
      )}
      {getShareCart()}
      {getTitleLables()}
      {renderItemSummaryContainer()}
      {getSubTotal()}
      {getDiscountLineItem()}
      {getShippingAndHandlingLineItem()}
      {getShippingInfo()}
      {getPromotionSection()}
      {openShippingOptionModal()}
      {getDivider()}
      {typeof data.subTotal !== 'undefined' && getOrderTotal()}
    </div>
  );
};
OrderSummaryCart.propTypes = {
  className: PropTypes.string,
  labels: PropTypes.shape({
    orderSummary: PropTypes.string,
    subtotal: PropTypes.string,
    discount: PropTypes.string,
    promotionalCode: PropTypes.string,
    preOrderTotal: PropTypes.string,
    item: PropTypes.string,
    items: PropTypes.string,
    promoCode: PropTypes.string,
    details: PropTypes.string,
    shippingHandling: PropTypes.string,
    estimatedTax: PropTypes.string,
    getPromotionalDiscount: PropTypes.string,
    signIn: PropTypes.string,
    yourPrice: PropTypes.string,
    toView: PropTypes.string,
    quoteSummary: PropTypes.string,
    promotionDetails: PropTypes.string,
    inValidPromoError: PropTypes.string,
    checkoutDiscount: PropTypes.string,
    promotionalCodePlaceholder: PropTypes.string,
    signInViewMessage: PropTypes.string,
    cartOrderTotal: PropTypes.string,
  }),
  data: PropTypes.shape({
    totalItems: PropTypes.number,
    totalPrice: PropTypes.string,
    totalDiscounts: PropTypes.string,
    totalPriceWithTax: PropTypes.string,
    subTotal: PropTypes.string,
    totalBasePriceWithTax: PropTypes.string,
    totalShippingAndHandling: PropTypes.string,
    totalTax: PropTypes.string,
    totalTaxValue: PropTypes.number,
    coupons: PropTypes.arrayOf(PropTypes.string),
    couponsError: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    promoCodeDetails: PropTypes.shape({
      allowUser: PropTypes.bool,
      description: PropTypes.string,
      displayOnWeb: PropTypes.bool,
      endDate: PropTypes.string,
      isValid: PropTypes.bool,
      startDate: PropTypes.string,
      terms: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
  userState: PropTypes.string.isRequired,
  userIcon: PropTypes.string,
  collapseIcon: PropTypes.string,
  openIcon: PropTypes.string,
  signInUrl: PropTypes.string,
  isCheckout: PropTypes.bool,
  isCart: PropTypes.bool,
  isOrderConfirmation: PropTypes.bool,
  pageContext: PropTypes.string,
  addPromoCode: PropTypes.func,
  getpromoCodeDetails: PropTypes.func,
  promotionIconUrl: PropTypes.string,
  errorIcon: PropTypes.string,
  closeIcon: PropTypes.string,
  showPromotionalCode: PropTypes.bool,
  isQuoteToOrder: PropTypes.bool,
  includeTaxWithPrice: PropTypes.bool,
  shareCartIcon: PropTypes.string,
  isShareCartEnabled: PropTypes.bool,
  handleShareCartButtonClick: PropTypes.func,
  openShippingOptionModal: PropTypes.func,
  shippingInfo: PropTypes.arrayOf(PropTypes.string),
  renderItemSummaryContainer: PropTypes.func,
  isPromotionDisabled: PropTypes.bool,
  renderTaxExemptCertificate: PropTypes.node,
};

OrderSummaryCart.defaultProps = {
  className: '',
  labels: {
    orderSummary: '',
    subtotal: '',
    discount: '',
    promotionalCode: '',
    preOrderTotal: '',
    promoCode: '',
    details: '',
    shippingHandling: '',
    estimatedTax: '',
    getPromotionalDiscount: '',
    signIn: '',
    yourPrice: '',
    toView: '',
    promotionDetails: '',
    inValidPromoError: '',
    checkoutDiscount: '',
    promotionalCodePlaceholder: '',
    signInViewMessage: '',
    cartOrderTotal: '',
  },
  data: { totalItems: 0 },
  userIcon: '',
  collapseIcon: '',
  openIcon: '',
  signInUrl: '',
  isCheckout: false,
  isCart: false,
  isOrderConfirmation: false,
  pageContext: '',
  addPromoCode: () => {
    /* default addPromoCode handler */
  },
  getpromoCodeDetails: () => {
    /* default getpromoCodeDetails handler */
  },
  promotionIconUrl: '',
  errorIcon: '',
  closeIcon: '',
  showPromotionalCode: true,
  isQuoteToOrder: false,
  includeTaxWithPrice: false,
  shareCartIcon: '',
  isShareCartEnabled: false,
  handleShareCartButtonClick: () => {
    /* default handleShareCartButtonClick handler */
  },
  openShippingOptionModal: () => {
    /* default openShippingOptionModal handler */
  },
  shippingInfo: [],
  renderItemSummaryContainer: () => {
    /* default renderItemSummaryContainer handler */
  },
  isPromotionDisabled: false,
  renderTaxExemptCertificate: <div />,
};

export default OrderSummaryCart;
